/**
 * dialogs — real, V2-styled replacements for window.alert / confirm / prompt.
 *
 * Imperative, promise-based API so call sites read like the natives:
 *   await vAlert('Saved!');
 *   if (!(await vConfirm('Delete 3 videos?'))) return;
 *   const name = await vPrompt('Folder name');           // null on cancel
 *
 * Each call mounts its own React root on document.body (no provider needed)
 * and unmounts on resolve. Enter confirms, Escape cancels.
 */
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertTriangle, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/v2/utils';

type DialogKind = 'alert' | 'confirm' | 'prompt';

interface DialogOpts {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  defaultValue?: string;
  placeholder?: string;
}

function DialogView({ kind, message, opts, resolve }: {
  kind: DialogKind;
  message: string;
  opts: DialogOpts;
  resolve: (v: string | boolean | null) => void;
}) {
  const { t } = useTranslation('v2');
  const [value, setValue] = useState(opts.defaultValue ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  const cancel = () => resolve(kind === 'prompt' ? null : false);
  const confirm = () => resolve(kind === 'prompt' ? value : true);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel();
      if (e.key === 'Enter' && kind !== 'prompt') confirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in" onClick={cancel}>
      <div
        className="w-full max-w-sm rounded-3xl bg-[#15151d] border border-white/10 p-6 shadow-2xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3.5">
          <span className={cn(
            'w-10 h-10 rounded-2xl flex items-center justify-center shrink-0',
            opts.danger ? 'bg-red-500/15 border border-red-500/30' : 'bg-primary/15 border border-primary/30'
          )}>
            {opts.danger
              ? <AlertTriangle className="w-5 h-5 text-red-400" />
              : <Info className="w-5 h-5 text-primary" />}
          </span>
          <div className="flex-1 min-w-0 pt-0.5">
            {opts.title && <h3 className="text-base font-bold text-text-primary mb-1">{opts.title}</h3>}
            <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">{message}</p>
          </div>
          <button onClick={cancel} className="shrink-0 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-text-tertiary hover:text-text-primary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {kind === 'prompt' && (
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}
            placeholder={opts.placeholder}
            className="mt-4 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50 transition-colors"
          />
        )}

        <div className="flex items-center justify-end gap-2.5 mt-5">
          {kind !== 'alert' && (
            <button onClick={cancel} className="px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors">
              {opts.cancelLabel ?? t('dialogs.cancel')}
            </button>
          )}
          <button
            onClick={confirm}
            className={cn(
              'px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-all hover:opacity-95',
              opts.danger ? 'bg-red-500 hover:bg-red-400' : 'bg-gradient-to-r from-primary to-accent hover:shadow-glow'
            )}
          >
            {opts.confirmLabel ?? (kind === 'alert' ? t('dialogs.ok') : t('dialogs.confirm'))}
          </button>
        </div>
      </div>
    </div>
  );
}

function show(kind: DialogKind, message: string, opts: DialogOpts = {}): Promise<string | boolean | null> {
  return new Promise((res) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const resolve = (v: string | boolean | null) => {
      root.unmount();
      host.remove();
      res(v);
    };
    root.render(<DialogView kind={kind} message={message} opts={opts} resolve={resolve} />);
  });
}

export const vAlert = (message: string, opts?: DialogOpts): Promise<void> =>
  show('alert', message, opts).then(() => undefined);

export const vConfirm = (message: string, opts?: DialogOpts): Promise<boolean> =>
  show('confirm', message, opts).then((v) => v === true);

export const vPrompt = (message: string, opts?: DialogOpts): Promise<string | null> =>
  show('prompt', message, opts).then((v) => (typeof v === 'string' ? v : null));
